import { PayloadAction } from '@reduxjs/toolkit'
import { call, put, select, takeLatest } from 'redux-saga/effects'

import i18n from '@/i18n'
import { userRoles } from '@/redux/auth/selectors'
import * as DoctorAPI from '@/services/doctor'
import * as PatientAPI from '@/services/patient'
import * as UserAPI from '@/services/user'
import { UserRole } from '@/types'
import {
  editUserDataFailed,
  editUserDataRequest,
  editUserDataSuccess,
  editUserSettingsFailed,
  editUserSettingsRequest,
  editUserSettingsSuccess,
  getUserDataFailed,
  getUserDataRequest,
  getUserDataSuccess
} from './actions'
import type {
  DoctorDataResponse,
  EditUserDataRequestPayload,
  EditUserSettingsRequestPayload,
  EditUserSettingsSuccessPayload,
  IEditableUserData,
  IUserData,
  PatientDataResponse,
  UserSettingsResponse
} from './types'

// Set i18n lang accordingly to value saved in DB
function forceUserLang (langCode: string) {
  if (i18n.resolvedLanguage !== langCode) {
    i18n.changeLanguage(langCode)
  }
}

function * getUserDataSaga () {
  const currentUserRoles: UserRole[] = yield select(userRoles)

  let userData: IUserData | null = null

  try {
    if (currentUserRoles.includes('doctor')) {
      const res: DoctorDataResponse = yield call(DoctorAPI.getDoctorData)
      const doctorSettings: UserSettingsResponse = yield call(
        DoctorAPI.getDoctorSettings
      )
      userData = {
        ...res.doctor,
        products: res.products,
        stripeConnectedAccount: res.stripeConnectedAccount,
        isSenior: res.isSenior,
        settings: doctorSettings
      }
    } else {
      const patientSettings: UserSettingsResponse = yield call(
        PatientAPI.getPatientSettings
      )
      const patientRes: PatientDataResponse = yield call(
        PatientAPI.getPatientData
      )
      userData = { ...patientRes, settings: patientSettings }
    }

    if (!userData) {
      throw Error
    }

    forceUserLang(userData.lang)

    yield put(getUserDataSuccess({ userData }))
  } catch (err) {
    yield put(getUserDataFailed())
  }
}

function * editUserDataSaga ({
  payload
}: PayloadAction<EditUserDataRequestPayload>) {
  const currentUserRoles: UserRole[] = yield select(userRoles)
  const { fieldsToEdit } = payload

  try {
    const userData: IEditableUserData = yield call(UserAPI.editUserData, {
      roles: currentUserRoles,
      fieldsToEdit
    })

    if (!userData) {
      throw Error
    }

    forceUserLang(userData.lang)

    yield put(editUserDataSuccess({ userData }))
  } catch (err) {
    yield put(editUserDataFailed())
  }
}

function * editSettingsUserSaga ({
  payload
}: PayloadAction<EditUserSettingsRequestPayload>) {
  const currentUserRoles: UserRole[] = yield select(userRoles)

  const { settingsToEdit } = payload

  try {
    const userSettings: EditUserSettingsSuccessPayload = yield call(
      UserAPI.editUserSettings,
      {
        roles: currentUserRoles,
        userSettingsToEdit: settingsToEdit
      }
    )

    if (!userSettings) {
      throw Error
    }

    yield put(editUserSettingsSuccess({ ...userSettings }))
  } catch (err) {
    yield put(editUserSettingsFailed())
  }
}

function * userSaga () {
  yield takeLatest(getUserDataRequest.type, getUserDataSaga)
  yield takeLatest(editUserDataRequest.type, editUserDataSaga)
  yield takeLatest(editUserSettingsRequest.type, editSettingsUserSaga)
}

export default userSaga
